/**
 * Email Template React Query hooks
 * ADR-090: Email Template System
 */

import type { TemplateBlocks } from '@/api/email-templates';
import {
  getEmailTemplate,
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  sendTestEmail,
  updateEmailTemplate,
} from '@/api/email-templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useEmailTemplateList(language?: string) {
  return useQuery({
    queryKey: ['email-templates', language],
    queryFn: async () => {
      const res = await listEmailTemplates(language);
      return res.data;
    },
  });
}

export function useEmailTemplate(slug: string | null, language: string) {
  return useQuery({
    queryKey: ['email-template', slug, language],
    queryFn: async () => {
      if (!slug) return null;
      const res = await getEmailTemplate(slug, language);
      return res.data;
    },
    enabled: slug !== null,
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      slug: string;
      language: string;
      body: {
        name?: string | undefined;
        subject?: string | undefined;
        blocks?: Partial<TemplateBlocks> | undefined;
        isActive?: boolean | undefined;
      };
    }) => {
      const res = await updateEmailTemplate(params.slug, params.language, params.body);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({ queryKey: ['email-template'] });
    },
  });
}

export function useResetEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { slug: string; language: string }) => {
      await resetEmailTemplate(params.slug, params.language);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      void queryClient.invalidateQueries({ queryKey: ['email-template'] });
    },
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    mutationFn: async (params: {
      slug: string;
      language: string;
      variables?: Record<string, string> | undefined;
    }) => {
      const res = await previewEmailTemplate(params.slug, params.language, params.variables);
      return res.data;
    },
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (params: { slug: string; email?: string | undefined }) => {
      const res = await sendTestEmail(params.slug, params.email);
      return res.data;
    },
  });
}
